import { useId } from 'react'

import { cn } from '@/lib/cn'

/**
 * Lambang Skava.
 *
 * Hurufnya adalah S yang sengaja terputus di tengah: dua lengkung yang
 * saling menghadap namun tidak menyatu. Celah itulah gagasan produknya —
 * dana berhenti sejenak di antara dua pihak sebelum berpindah. Bentuknya
 * tetap terbaca sebagai S pada ukuran 16 piksel karena hanya terdiri atas
 * dua goresan tebal berujung bulat.
 */
export function LogoMark({ className, rounded = true }: { className?: string; rounded?: boolean }) {
  // Tiap lambang memakai id gradiennya sendiri. Dengan id bersama, lambang
  // yang terlihat bisa menunjuk ke gradien milik lambang lain yang sedang
  // disembunyikan — misalnya lajur kiri aplikasi di layar ponsel — dan
  // peramban lalu menggambarnya tanpa latar sama sekali.
  const gradien = `skava-mark-bg-${useId()}`

  return (
    <svg
      viewBox="0 0 32 32"
      className={cn('size-8 shrink-0', className)}
      role="img"
      aria-label="Skava"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradien} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2ad175" />
          <stop offset="60%" stopColor="#16c264" />
          <stop offset="100%" stopColor="#0f9e50" />
        </linearGradient>
      </defs>

      <rect width="32" height="32" rx={rounded ? 9 : 0} fill={`url(#${gradien})`} />

      {/* Dua lengkung digambar terpisah agar celah di tengah tetap terjaga
          pada setiap ukuran; menyatukannya menjadi satu path membuat celah
          tertutup saat dirender kecil. */}
      <g
        fill="none"
        stroke="#0d341e"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21.6 11.4c-.5-2.1-2.7-3.4-5.6-3.4-3.2 0-5.4 1.6-5.4 3.9 0 1.7 1.2 2.8 3.6 3.4" />
        <path d="M10.4 20.6c.5 2.1 2.7 3.4 5.6 3.4 3.2 0 5.4-1.6 5.4-3.9 0-1.7-1.2-2.8-3.6-3.4" />
      </g>
    </svg>
  )
}

/**
 * Logo lengkap: lambang beserta kata "skava.id".
 *
 * Akhiran .id ditulis dengan warna aksen dan bobot lebih ringan supaya nama
 * mereknya tetap yang pertama terbaca, sementara domainnya tetap terlihat
 * sebagai bagian dari logo — bukan tempelan.
 */
export function Logo({
  className,
  compact = false,
  onDark = false,
}: {
  className?: string
  compact?: boolean
  onDark?: boolean
}) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <LogoMark />
      {!compact ? (
        <span
          className={cn(
            'text-lg font-bold leading-none tracking-[-0.04em]',
            onDark ? 'text-white' : 'text-ink',
          )}
        >
          skava
          <span className={cn(onDark ? 'text-white/70' : 'text-brand')}>.id</span>
        </span>
      ) : null}
    </span>
  )
}
